import React from "react";
import {
  IonMenu,
  IonHeader,
  IonToolbar,
  IonContent,
  IonFooter,
  IonButton,
  IonItemGroup,
  IonImg,
} from "@ionic/react";
import { menuController } from "@ionic/core";
import { connect } from "react-redux";
import { useHistory } from "react-router-dom";
import { useIonAlert } from "@ionic/react";

import config from "config";

import FrontendMenu from "./FrontendMenu";

import { logout } from "services/auth";
import { setLoading } from "services/loading";
import { submitResults } from "services/quiz";
import Spacer from "components/spacer";
import { useToasts } from "components/Toast/toast";
import ChromeCast from "services/chromecast";

interface Props {
  user: any;
  teams: any;
  quizData: any;
  logout: Function;
  setLoading: any;
  submitResults: any;
}

const AppMenu: React.FC<Props> = ({
  user,
  teams,
  quizData,
  logout,
  setLoading,
  submitResults,
}) => {
  const history = useHistory();
  const [presentAlert] = useIonAlert();
  const { addToast } = useToasts();

  const closeMenu = async () => {
    await menuController.toggle();
  };

  async function logOut() {
    if (ChromeCast.isConnected()) {
      ChromeCast.quit();
    }
    closeMenu();
    setLoading(true, "Logging out");
    setTimeout(() => {
      logout();
      setLoading(false);
    }, 500);
  }

  const onFinalizeSuccess = () => {
    addToast({
      message: "Finalise Successful",
      duration: 3000,
      color: "success",
    });
    logOut();
  };

  const onFinalizeFail = () => {
    addToast({
      message: "Finalise Failed",
      duration: 3000,
      color: "danger",
    });
    presentAlert({
      header: "Alert",
      subHeader: "Finalise Failed",
      message: "The quiz results were <b>NOT finalised</b> successfully.",
      buttons: [
        {
          text: "OK",
          role: "cancel",
        },
        {
          text: "Retry",
          role: "confirm",
          handler: () => {
            setTimeout(() => {
              onLogOutClick();
            }, 500);
          },
        },
      ],
    });
  };

  const onFinaliseResults = () => {
    setTimeout(() => {
      if (teams?.length > 0) {
        if (quizData.resultsSubmitted) {
          presentAlert({
            header: "Alert",
            subHeader: "Refinalise Results",
            message:
              "You are about to refinalise the results. This will overwrite the previous results. Are you sure you want to continue?",
            buttons: [
              {
                text: "OK",
                role: "confirm",
                handler: (alertData) => {
                  if (alertData?.reason !== "") {
                    submitResults(teams, quizData.quizConfig, user)
                      .then((res: any) => {
                        if (res.status === 200) {
                          onFinalizeSuccess();
                        } else {
                          onFinalizeFail();
                        }
                      })
                      .catch(() => {
                        onFinalizeFail();
                      });
                  } else {
                    addToast({
                      message: "Please provide a reason for refinalising",
                      duration: 3000,
                      color: "danger",
                    });
                  }
                },
              },
              {
                text: "Cancel",
                role: "cancel",
              },
            ],
            inputs: [
              {
                name: "reason",
                type: "textarea",
                placeholder: "Tell us why you want to refinalise the results",
              },
            ],
          });
        } else {
          presentAlert({
            header: "Alert",
            subHeader: "Finalise Results",
            message:
              "Are you sure you want to finalise results? This action cannot be done again.",
            buttons: [
              {
                text: "Cancel",
                role: "cancel",
              },
              {
                text: "OK",
                role: "confirm",
                handler: () => {
                  submitResults(teams, quizData.quizConfig, user)
                    .then((res: any) => {
                      if (res.status === 200) {
                        onFinalizeSuccess();
                      } else {
                        onFinalizeFail();
                      }
                    })
                    .catch(() => {
                      onFinalizeFail();
                    });
                },
              },
            ],
          });
        }
      } else {
        presentAlert({
          header: "Alert",
          subHeader: "No Teams Found",
          message: "Please add teams and their results before finalising.",
          buttons: [
            {
              text: "Cancel",
              role: "cancel",
            },
            {
              text: "OK",
              role: "cancel",
            },
          ],
        });
      }
    }, 500);
  };

  const onLogOutClick = () => {
    if (quizData.resultsSubmitted && !quizData.resultsAmended) {
      logOut();
    } else if (quizData.progress.round === 0) {
      if (teams.length > 0) {
        presentAlert({
          header: "Alert",
          subHeader: "Are you sure you want to logout?",
          message: "Data you have entered may be lost. Are you sure?",
          buttons: [
            {
              text: "Logout",
              role: "confirm",
              handler: (alertData) => {
                if (alertData.acceptance.toLowerCase() === "yes") {
                  logOut();
                } else {
                  console.log(alertData.acceptance);
                  addToast({
                    message: "You must type YES to log out",
                    duration: 3000,
                    color: "danger",
                  });
                }
              },
            },
            {
              text: "Cancel",
              role: "cancel",
            },
          ],
          inputs: [
            {
              name: "acceptance",
              type: "text",
              placeholder: "Type YES and click LOGOUT",
            },
          ],
        });
      } else {
        logOut();
      }
    } else {
      presentAlert({
        header: "Alert!",
        subHeader: "You have not finalised the quiz results",
        message:
          "You have not finalised the quiz results. Are you sure you want to log out?",
        buttons: [
          {
            text: "Logout",
            role: "confirm",
            handler: (alertData) => {
              if (alertData.acceptance.toLowerCase() === "yes") {
                logOut();
              } else {
                console.log(alertData.acceptance);
                addToast({
                  message: "You must type YES to log out",
                  duration: 3000,
                  color: "danger",
                });
              }
            },
          },
          {
            text: "Submit",
            role: "confirm",
            handler: () => {
              onFinaliseResults();
            },
          },
        ],
        inputs: [
          {
            name: "acceptance",
            type: "text",
            placeholder: "Type YES and click LOGOUT",
          },
        ],
      });
    }
  };

  return (
    <IonMenu side="start" contentId="main-content">
      <IonHeader>
        <IonToolbar color="primary">
          <IonImg
            className="header-image linked"
            onClick={() => history.push("/dashboard")}
            src={config.MENU_LOGO}
          ></IonImg>
        </IonToolbar>
      </IonHeader>
      <IonContent>
        <IonItemGroup>
          <FrontendMenu />
        </IonItemGroup>
      </IonContent>
      <IonFooter>
        <IonToolbar color="light" className="ion-padding">
          <IonButton onClick={onLogOutClick}>Logout</IonButton>
          <Spacer size="30" />
        </IonToolbar>
      </IonFooter>
    </IonMenu>
  );
};

const mapStateToProps = (state: any) => ({
  user: state.user,
  quizData: state.quiz,
  teams: state.teams.teams,
});

export default connect(mapStateToProps, { logout, setLoading, submitResults })(
  AppMenu
);
